import { QuizzesService } from "@/client"
import type { ProgramsPublic } from "@/client"
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { toaster } from "@/components/ui/toaster"
import { Tooltip } from "@/components/ui/tooltip"
import {
  Button,
  IconButton,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { LuRotateCcw, LuTrash2 } from "react-icons/lu"

interface ClearAttemptsControlsProps {
  quizId: string
  totalAttempts: number
  onDeleted: () => void
}

export function ClearAttemptsControls({
  quizId,
  totalAttempts,
  onDeleted,
}: ClearAttemptsControlsProps) {
  const [clearAllOpen, setClearAllOpen] = useState(false)
  const [byProgramOpen, setByProgramOpen] = useState(false)
  const [selectedProgramId, setSelectedProgramId] = useState<string>("")

  const queryClient = useQueryClient()

  const { data: programsData } = useQuery({
    queryKey: ["quiz-programs", quizId],
    queryFn: () =>
      QuizzesService.listQuizPrograms({ quizId }) as Promise<ProgramsPublic>,
    retry: false,
  })
  const programs = programsData?.data ?? []

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["quiz-results", quizId] })
    queryClient.invalidateQueries({ queryKey: ["quiz-analytics", quizId] })
    queryClient.invalidateQueries({
      queryKey: ["quiz-cohort-distribution", quizId],
    })
  }

  const clearAllMutation = useMutation({
    mutationFn: () =>
      QuizzesService.clearAllAttempts({ quizId }) as Promise<{
        deleted_count: number
      }>,
    onSuccess: (data) => {
      toaster.create({
        title: `Cleared ${data.deleted_count} attempt${
          data.deleted_count !== 1 ? "s" : ""
        }`,
        type: "success",
      })
      setClearAllOpen(false)
      invalidate()
      onDeleted()
    },
    onError: () => {
      toaster.create({ title: "Failed to clear attempts", type: "error" })
    },
  })

  const clearByProgramMutation = useMutation({
    mutationFn: () =>
      QuizzesService.clearAttemptsByProgram({
        quizId,
        programId: selectedProgramId,
      }) as Promise<{ deleted_count: number }>,
    onSuccess: (data) => {
      const prog = programs.find((p) => p.id === selectedProgramId)
      toaster.create({
        title: `Cleared ${data.deleted_count} attempt${
          data.deleted_count !== 1 ? "s" : ""
        } from ${prog?.name ?? "program"}`,
        type: "success",
      })
      setByProgramOpen(false)
      setSelectedProgramId("")
      invalidate()
      onDeleted()
    },
    onError: () => {
      toaster.create({
        title: "Failed to clear program attempts",
        type: "error",
      })
    },
  })

  return (
    <Stack direction="row" gap={2}>
      {/* Clear by Program */}
      <DialogRoot
        open={byProgramOpen}
        onOpenChange={({ open }) => {
          setByProgramOpen(open)
          if (!open) setSelectedProgramId("")
        }}
        role="alertdialog"
      >
        <Button
          variant="outline"
          colorPalette="red"
          size="sm"
          onClick={() => setByProgramOpen(true)}
          disabled={programs.length === 0}
        >
          Clear by Program
        </Button>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Attempts by Program</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              <Text>
                Select a program to permanently delete all attempts from its
                enrolled students. This cannot be undone.
              </Text>
              <NativeSelectRoot>
                <NativeSelectField
                  value={selectedProgramId}
                  onChange={(e) => setSelectedProgramId(e.target.value)}
                >
                  <option value="">Select a program…</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Cancel</Button>
            </DialogActionTrigger>
            <Button
              colorPalette="red"
              onClick={() => clearByProgramMutation.mutate()}
              loading={clearByProgramMutation.isPending}
              disabled={!selectedProgramId}
            >
              Clear Attempts
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Clear All */}
      <DialogRoot
        open={clearAllOpen}
        onOpenChange={({ open }) => setClearAllOpen(open)}
        role="alertdialog"
      >
        <Button
          colorPalette="red"
          size="sm"
          onClick={() => setClearAllOpen(true)}
        >
          Clear All Attempts
        </Button>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Attempts</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text>
              This will permanently delete all <strong>{totalAttempts}</strong>{" "}
              attempt
              {totalAttempts !== 1 ? "s" : ""} for this quiz. This cannot be
              undone.
            </Text>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Cancel</Button>
            </DialogActionTrigger>
            <Button
              colorPalette="red"
              onClick={() => clearAllMutation.mutate()}
              loading={clearAllMutation.isPending}
            >
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Stack>
  )
}

interface DeleteAttemptButtonProps {
  quizId: string
  attemptId: string
  studentIdentifier: string
  onDeleted: () => void
}

export function DeleteAttemptButton({
  quizId,
  attemptId,
  studentIdentifier,
  onDeleted,
}: DeleteAttemptButtonProps) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => QuizzesService.deleteSingleAttempt({ quizId, attemptId }),
    onSuccess: () => {
      toaster.create({ title: "Attempt deleted", type: "success" })
      setOpen(false)
      queryClient.invalidateQueries({ queryKey: ["quiz-results", quizId] })
      queryClient.invalidateQueries({ queryKey: ["quiz-analytics", quizId] })
      queryClient.invalidateQueries({
        queryKey: ["quiz-cohort-distribution", quizId],
      })
      onDeleted()
    },
    onError: () => {
      toaster.create({ title: "Failed to delete attempt", type: "error" })
    },
  })

  return (
    <DialogRoot
      open={open}
      onOpenChange={({ open }) => setOpen(open)}
      role="alertdialog"
    >
      <Tooltip
        openDelay={300}
        content="Delete: permanently removes this attempt and all of its saved answers. Cannot be undone. To let the student continue instead, use Reopen."
      >
        <IconButton
          aria-label="Delete attempt"
          variant="ghost"
          colorPalette="red"
          size="sm"
          onClick={() => setOpen(true)}
        >
          <LuTrash2 />
        </IconButton>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Attempt</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>
            Permanently delete the attempt from{" "}
            <strong>{studentIdentifier}</strong>? This cannot be undone.
          </Text>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Cancel</Button>
          </DialogActionTrigger>
          <Button
            colorPalette="red"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

interface ReopenAttemptButtonProps {
  quizId: string
  attemptId: string
  studentIdentifier: string
  onReopened: () => void
}

export function ReopenAttemptButton({
  quizId,
  attemptId,
  studentIdentifier,
  onReopened,
}: ReopenAttemptButtonProps) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => QuizzesService.reopenSingleAttempt({ quizId, attemptId }),
    onSuccess: () => {
      toaster.create({
        title: "Attempt reopened",
        description: "The student can now resume and resubmit this attempt.",
        type: "success",
      })
      setOpen(false)
      queryClient.invalidateQueries({ queryKey: ["quiz-results", quizId] })
      queryClient.invalidateQueries({ queryKey: ["quiz-analytics", quizId] })
      queryClient.invalidateQueries({
        queryKey: ["quiz-cohort-distribution", quizId],
      })
      onReopened()
    },
    onError: () => {
      toaster.create({ title: "Failed to reopen attempt", type: "error" })
    },
  })

  return (
    <DialogRoot
      open={open}
      onOpenChange={({ open }) => setOpen(open)}
      role="alertdialog"
    >
      <Tooltip
        openDelay={300}
        content="Reopen: keeps this attempt and all saved answers, but clears the completion and score so the student can resume where they left off and resubmit. Use this to recover from a premature or accidental submission. (Delete, by contrast, removes the attempt for good.)"
      >
        <IconButton
          aria-label="Reopen attempt"
          variant="ghost"
          colorPalette="blue"
          size="sm"
          onClick={() => setOpen(true)}
        >
          <LuRotateCcw />
        </IconButton>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen Attempt</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={3}>
            <Text>
              Reopen the attempt from <strong>{studentIdentifier}</strong> so
              they can continue it?
            </Text>
            <Text color="gray.600" fontSize="sm">
              The attempt and all of its saved answers are kept. Its completion
              status, score, and percentile are cleared until the student
              resubmits. This does not delete the attempt.
            </Text>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Cancel</Button>
          </DialogActionTrigger>
          <Button
            colorPalette="blue"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
          >
            Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
